import { Card } from '@startuptools/ui/Card';
import { Typography } from '@startuptools/ui/Typography';
import { OptionsRegistrationForm } from '../../../../common/components/documents/OptionsRegistrationForm';
import { Divider } from '@startuptools/ui/Divider';
import { StateStepper } from '../../../../../components/steppers/state-stepper/StateStepper';
import { StateStepperStep } from '../../../../../components/steppers/state-stepper/StateStepperStep';
import { Trans } from '@lingui/react';
import { OrStepPayment } from '../steps/StepOrPayment';
import { OrStepAgreementSignatories } from '../steps/StepOrAgreement';
import { OrStepSubscribers } from '../steps/or-subscribers';
import { StepRegForm } from './StepRegForm';
import { StepNextStepsOr } from './StepNextStepsOr';
import {
  OptionsRegistrationDocument,
  OptionsRegistrationsDocument,
  useOptionsRegistrationSuspenseQuery,
} from '../../../OptionsRegistration.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMemo, useState } from 'react';
import { GqlSnrCaseSubjectKind, GqlWarrantProgramKind } from '../../../../../graphql/base-types.graphql';
import { PaymentKind } from '@startuptools/common/participants';
import { useOrDeleteMutation, useOrSetSnrCaseMutation } from '../../../../../graphql/react-operations';
import { AlertConfirmDialog } from '../../../../../components/simple-dialogs/AlertConfirmDialog';
import { ConnectSnrCaseDialog } from '../../../../../components/connect-snr-case-dialog/ConnectSnrCaseDialog';
import { DateTime } from 'luxon';
import { Button } from '@startuptools/ui/Button';
import { wpEditDecisionLinkOld } from '../../../../../components/Links';
import { useNavigate } from '../../../../../components/Navigate';

export const OptionsRegistration = ({ orId, warrantProgramId }: { orId: string; warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const { navigate } = useNavigate();

  const [showDelete, setShowDelete] = useState(false);
  const [showConnectSnrCase, setShowConnectSnrCase] = useState(false);

  const {
    data: { optionsRegistration, warrantProgram, optionsRegistrationErrors: orErrors },
  } = useOptionsRegistrationSuspenseQuery({ variables: { companyId, warrantProgramId, orId } });
  const [doDelete, { loading }] = useOrDeleteMutation({
    refetchQueries: [OptionsRegistrationsDocument],
    awaitRefetchQueries: true,
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doConnectSnrCase] = useOrSetSnrCaseMutation({
    refetchQueries: [OptionsRegistrationDocument],
    awaitRefetchQueries: true,
  });

  const canDelete = useMemo(() => {
    if (warrantProgram.warrantKind !== GqlWarrantProgramKind.Warrant || !warrantProgram.usesStepper) {
      return false;
    }
    return !optionsRegistration.participants.some(wpp => wpp.paymentKind !== PaymentKind.Cash);
  }, [warrantProgram.usesStepper, warrantProgram.warrantKind, optionsRegistration.participants]);

  return (
    <>
      <Card>
        <Card>
          <Stack direction="row" justifyContent="space-between">
            <Box>
              <Typography level="h4">{optionsRegistration.description}</Typography>
            </Box>
            <Stack direction="row" gap={1}>
              {warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee && (
                <>
                  {optionsRegistration.snrCase ? (
                    <Stack direction="row" gap={1} alignItems="center">
                      {optionsRegistration.snrCase?.filingNumber}/{optionsRegistration.snrCase?.filingYear}
                      <Button
                        variant="outlined"
                        color="neutral"
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          return doConnectSnrCase({
                            variables: { companyId, input: { id: optionsRegistration.id, snrCaseId: null } },
                          });
                        }}
                      >
                        <Trans id="Disconnect Case" />
                      </Button>
                    </Stack>
                  ) : (
                    <>
                      {warrantProgram.meetingConcern && (
                        <Button variant="outlined" color="neutral" onClick={() => setShowConnectSnrCase(true)}>
                          <Trans id="Connect Case" />
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}

              {canDelete && (
                <IconButton color="danger" onClick={() => setShowDelete(true)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          </Stack>
          {(warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant ||
            warrantProgram.warrantKind === GqlWarrantProgramKind.Wise) && (
            <>
              <Divider />
              <OptionsRegistrationForm
                optionsRegistration={optionsRegistration}
                warrantProgramId={optionsRegistration.warrantProgramId}
                navigateToDecisionStep={() => null}
              />
            </>
          )}
        </Card>

        <StateStepper onStepSaved={() => Promise.resolve(null)} onFinalStep={() => Promise.resolve(null)}>
          {warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant && (
            <StateStepperStep
              id="payment-account"
              label={<Trans id="Payment Account" />}
              validatorStatus={orErrors.payBeforeDate}
            >
              <OrStepPayment orId={optionsRegistration.id} warrantProgramId={optionsRegistration.warrantProgramId} />
            </StateStepperStep>
          )}

          {warrantProgram.usesStepper && (
            <StateStepperStep
              id="signatories"
              label={<Trans id="Agreement Signature" />}
              validatorStatus={orErrors.signatories}
            >
              <OrStepAgreementSignatories
                orId={optionsRegistration.id}
                warrantProgramId={optionsRegistration.warrantProgramId}
              />
            </StateStepperStep>
          )}

          <StateStepperStep id="subscribers" label={<Trans id="Subscribers" />} validatorStatus={orErrors.participants}>
            <OrStepSubscribers
              orId={optionsRegistration.id}
              wpId={optionsRegistration.warrantProgramId}
              navigateToDecisionStep={() => navigate(wpEditDecisionLinkOld(warrantProgram))}
            />
          </StateStepperStep>

          {warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee && (
            <StateStepperStep id="reg-form" label={<Trans id="Registration" />} validatorStatus={orErrors.signsRegForm}>
              <StepRegForm orId={optionsRegistration.id} />
            </StateStepperStep>
          )}

          {warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee && (
            <StateStepperStep id="next-step" label={<Trans id="Next Step" />} validatorStatus={orErrors.todos}>
              <StepNextStepsOr optionsRegistrationId={optionsRegistration.id} />
            </StateStepperStep>
          )}
        </StateStepper>
      </Card>
      {showDelete && (
        <AlertConfirmDialog
          loading={loading}
          onClose={() => setShowDelete(false)}
          onConfirm={() => doDelete({ variables: { companyId, id: optionsRegistration.id } })}
        />
      )}
      {showConnectSnrCase && (
        <ConnectSnrCaseDialog
          onClose={() => setShowConnectSnrCase(false)}
          subjectKinds={[GqlSnrCaseSubjectKind.Topt]}
          onClick={({ id: snrCaseId }) =>
            doConnectSnrCase({ variables: { companyId, input: { id: optionsRegistration.id, snrCaseId } } })
          }
          fromDate={warrantProgram.meetingConcern?.meeting.startTime ?? DateTime.fromObject({ year: 1900 }).toString()}
        />
      )}
    </>
  );
};
